<template>
  <footer class="bg-navy text-white">
    <div class="container mx-auto px-4 md:px-6 pt-16 pb-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
        <div>
          <div class="mb-6 flex items-center">
            <picture class="shrink-0 drop-shadow-[0_1px_3px_rgba(255,255,255,0.07)]">
              <source srcset="/borowki.avif" type="image/avif" />
              <img src="/borowki.png" alt="Borówka Amerykańska Logo" class="h-20 p-2" />
            </picture>
            <p
              class="text-2xl md:text-3xl lg:text-xl font-bold leading-tight animate-fade-in flex items-center">
              <span class="text-blueberry-lighter">BorowkaSzczecin</span>
            </p>
          </div>
          <p class="text-secondary/80 mb-6">
            {{ t('footer.tagline') }}
          </p>
          <div class="flex space-x-4">
            <a href="https://www.facebook.com/borowka.szczecin"
              aria-label="Odwiedź nasz profil na Facebooku"
              class="bg-white/10 p-2.5 rounded-full hover:bg-white/20 transition-colors">
              <Facebook class="w-5 h-5" aria-hidden="true" />
            </a>
            <a href="https://www.instagram.com/borowka.szczecin/"
              aria-label="Odwiedź nasz profil na Instagramie"
              class="bg-white/10 p-2.5 rounded-full hover:bg-white/20 transition-colors">
              <Instagram class="w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>

        <div>
          <h2
            class="text-xl font-semibold mb-6 after:content-[''] after:block after:w-10 after:h-1 after:bg-leaf after:mt-2 after:rounded-full">
            {{ t('footer.links') }}
          </h2>
          <ul class="space-y-3">
            <li v-for="route in navRoutes" :key="route.name">
              <RouterLink :to="route" class="text-secondary/80 hover:text-white transition-colors">
                {{ t(route.labelKey) }}
              </RouterLink>
            </li>
            <li>
              <OrderGateLink
                class="border-0 bg-transparent p-0 text-left text-secondary/80 hover:text-white transition-colors"
              >
                {{ t(orderRoute.labelKey) }}
              </OrderGateLink>
            </li>
            <li>
              <RouterLink :to="orderStatusRoute" class="text-secondary/80 hover:text-white transition-colors">
                {{ t(orderStatusRoute.labelKey) }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <div id="contact">
          <h2
            class="text-xl font-semibold mb-6 after:content-[''] after:block after:w-10 after:h-1 after:bg-leaf after:mt-2 after:rounded-full">
            {{ t('footer.contact') }}
          </h2>
          <ul class="space-y-4">
            <li class="flex items-start">
              <MapPin class="w-5 h-5 mr-2 text-leaf mt-0.5" />
              <a href="https://maps.app.goo.gl/w3GcQd9BiP8hnYcT7"
                class="text-secondary/80 hover:text-white transition-colors">
                Pilchowo<br />
              </a>
            </li>
            <li class="flex items-center">
              <Phone class="w-5 h-5 mr-2 text-leaf" />
              <span class="text-secondary/80">+48 790 278 445</span>
            </li>
            <li class="flex items-center">
              <Mail class="w-5 h-5 mr-2 text-leaf" />
              <span class="text-secondary/80">kontakt@borowkaszczecin.pl</span>
            </li>
          </ul>
        </div>

        <div>
          <h2
            class="text-xl font-semibold mb-6 after:content-[''] after:block after:w-10 after:h-1 after:bg-leaf after:mt-2 after:rounded-full">
            {{ t('footer.hours') }}
          </h2>
          <ul class="space-y-2">
            <li class="flex justify-between">
              <span class="text-secondary/80">{{ t('footer.weekdays') }}</span>
              <span class="text-white">9:00 - 19:00</span>
            </li>
            <li class="flex justify-between">
              <span class="text-secondary/80">{{ t('footer.saturday') }}</span>
              <span class="text-white">9:00 - 19:00</span>
            </li>
            <li class="flex justify-between">
              <span class="text-secondary/80">{{ t('footer.sunday') }}</span>
              <span class="text-white">10:00 - 19:00</span>
            </li>
          </ul>
          <div class="mt-4 pt-4 md:mt-6 md:pt-6 border-t border-white/10">
            <p class="text-secondary/80 text-sm">
              {{ t('footer.harvestSeason') }}
            </p>
          </div>
        </div>
      </div>

      <div class="mt-6 md:mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center">
        <div class="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm mb-3 md:mb-0 md:order-2">
          <RouterLink v-for="route in termsRoutes" :key="route.name" class="text-secondary/60 hover:text-white transition-colors" :to="route">
            {{ t(route.labelKey) }}
          </RouterLink>
        </div>
        <p class="text-secondary/60 text-sm md:order-1">
          {{ t('footer.rights') }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import {
  aboutRoute,
  faqRoute,
  homeRoute,
  orderRoute,
  orderStatusRoute,
  termsRoutes,
} from "@/router/navigation";
import { Facebook, Instagram, MapPin, Phone, Mail } from "lucide-vue-next";
import { useI18n } from "vue-i18n";
import OrderGateLink from "@/components/OrderGateLink.vue";

const { t } = useI18n();
const navRoutes = [homeRoute, aboutRoute, faqRoute];
</script>
